import { useState, useEffect } from 'react';
import { Gift, Copy, Share2, Users, Star } from 'lucide-react';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { formatCurrency } from '@/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/common/Skeleton';
import toast from '@/hooks/useToast';

interface ReferralData {
  referralCode: string;
  referralCount: number;
  successfulReferrals: number;
  totalPointsEarned: number;
}

export const ReferralPage = () => {
  const { user } = useAuthStore();
  const [data, setData] = useState<ReferralData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/referral/my-code')
      .then(r => setData(r.data.data))
      .finally(() => setLoading(false));
  }, []);

  const referralLink = data ? `${window.location.origin}/register?ref=${data.referralCode}` : '';

  const copy = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Link copied!');
  };

  const share = () => {
    if (navigator.share) {
      navigator.share({ title: 'Milan Medical', text: `Use my referral code ${data?.referralCode} and get medicines delivered to your door at Milan Medical!`, url: referralLink });
    } else {
      copy();
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
          <Gift className="h-5 w-5 text-accent-600" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-2xl text-gray-800">Refer & Earn</h1>
          <p className="text-sm text-gray-500">Earn 200 Milan Points for every friend who orders</p>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-6 mb-6">
        <h3 className="font-semibold text-gray-800 mb-4">How it works</h3>
        <div className="space-y-3">
          {[
            { step: '1', text: 'Share your referral link with friends & family' },
            { step: '2', text: 'They register using your link and place their first order' },
            { step: '3', text: 'You earn 200 Milan Points instantly (worth ₹20 off)' },
          ].map(s => (
            <div key={s.step} className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-primary-500 text-white text-xs font-bold flex items-center justify-center shrink-0">{s.step}</div>
              <p className="text-sm text-gray-700">{s.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Referral code */}
      {loading ? (
        <Skeleton className="h-32" />
      ) : data && (
        <>
          <div className="bg-white rounded-card shadow-card p-6 mb-6">
            <p className="text-sm text-gray-500 mb-2">Your referral code</p>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3 font-mono font-bold text-xl text-primary-600 tracking-widest text-center">
                {data.referralCode}
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-3 break-all">{referralLink}</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={copy}>
                <Copy className="h-4 w-4 mr-2" /> Copy Link
              </Button>
              <Button className="flex-1" onClick={share}>
                <Share2 className="h-4 w-4 mr-2" /> Share
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-card shadow-card p-4 text-center">
              <Users className="h-6 w-6 text-primary-400 mx-auto mb-2" />
              <p className="font-bold text-2xl text-gray-800">{data.referralCount}</p>
              <p className="text-xs text-gray-500">Friends invited</p>
            </div>
            <div className="bg-white rounded-card shadow-card p-4 text-center">
              <Gift className="h-6 w-6 text-green-400 mx-auto mb-2" />
              <p className="font-bold text-2xl text-gray-800">{data.successfulReferrals}</p>
              <p className="text-xs text-gray-500">Successful</p>
            </div>
            <div className="bg-white rounded-card shadow-card p-4 text-center">
              <Star className="h-6 w-6 text-accent-400 mx-auto mb-2" />
              <p className="font-bold text-2xl text-gray-800">{data.totalPointsEarned}</p>
              <p className="text-xs text-gray-500">Points earned</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
